/// Reduz a foto antes de enviar.
///
/// Foto de celular hoje tem 3 a 8 MB. Para provar que uma caixa chegou amassada
/// ou a fita violada, 1280px a 70% de qualidade basta — dá uns 150 KB, cerca de
/// trinta vezes menos. A diferença aparece em dois lugares que importam: o
/// operador não fica esperando upload em rede fraca no galpão, e cinquenta
/// devoluções por mês ocupam megabytes em vez de centenas deles.
///
/// Feito no aparelho, e não no servidor, de propósito: comprimir depois de
/// subir não economiza o tempo de quem está esperando.

const LADO_MAXIMO = 1280;
const QUALIDADE = 0.7;

export async function comprimirFoto(arquivo: File): Promise<string> {
  const bitmap = await carregar(arquivo);

  const escala = Math.min(1, LADO_MAXIMO / Math.max(bitmap.width, bitmap.height));
  const largura = Math.round(bitmap.width * escala);
  const altura = Math.round(bitmap.height * escala);

  const canvas = document.createElement("canvas");
  canvas.width = largura;
  canvas.height = altura;

  const ctx = canvas.getContext("2d");
  if (!ctx) return lerComoDataUrl(arquivo);

  ctx.drawImage(bitmap, 0, 0, largura, altura);
  if ("close" in bitmap) bitmap.close();

  const comprimida = canvas.toDataURL("image/jpeg", QUALIDADE);

  // Imagem já pequena pode ficar MAIOR depois de recodificar. Nesse caso vale
  // mais mandar a original.
  const original = await lerComoDataUrl(arquivo);
  return comprimida.length < original.length ? comprimida : original;
}

async function carregar(arquivo: File): Promise<ImageBitmap | HTMLImageElement> {
  if ("createImageBitmap" in window) {
    try {
      // `imageOrientation` respeita o EXIF: sem isso, foto tirada de lado sobe
      // deitada e a prova fica difícil de ler.
      return await createImageBitmap(arquivo, { imageOrientation: "from-image" });
    } catch {
      // Segue para o caminho por <img>.
    }
  }

  const url = URL.createObjectURL(arquivo);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function lerComoDataUrl(arquivo: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result));
    leitor.onerror = () => reject(new Error("Não foi possível ler a imagem."));
    leitor.readAsDataURL(arquivo);
  });
}

/// Tamanho aproximado, em KB, de uma data URL — para mostrar ao operador.
export function tamanhoKb(dataUrl: string): number {
  const base64 = dataUrl.split(",")[1] ?? "";
  return Math.round((base64.length * 3) / 4 / 1024);
}
